import { Context, ExtraContext } from '@utilitywarehouse/tracking-core';
import { useCallback } from 'react';

import {
  ProductType,
  Quote,
  SelectBroadbandQuoteMutation,
} from '@generated/gql/graphql';
import { BroadbandFormValues } from '@src/types/broadband';
import { BundleBasketData } from '@src/types/bundle';
import { BooleanSelection } from '@src/types/index';
import { useTrackingContext } from '@tracking/Provider/Context';
import {
  SignupCoreServicesEntity,
  useTrackSignupBroadbandAddedToBasketOnce,
} from '@tracking/trackingEvents';
import { buildOptimizelyEntity } from '@tracking/utils/buildOptimizelyEntity';
import { findTrackingContextIndex } from '@tracking/utils/findTrackingContextIndex';
import { getGroupCombinations } from '@tracking/utils/getGroupCombinations';
import { parseValueToPennies } from '@utils/price';

import { getSelectedQuoteUnwrapped } from './getSelectedQuoteUnwrapped';

const getBroadbandMonthlyCost = (quotes: Quote[]) => {
  const broadbandQuote = quotes.find(
    (quote) => quote.product.type === ProductType.Broadband && quote.selected
  );

  return broadbandQuote?.totalMonthlyCost?.value || '';
};

const updateTrackingContext = (
  contexts: ExtraContext<Context>[],
  selectResult: SelectBroadbandQuoteMutation
): ExtraContext<Context>[] => {
  const coreServiceContextIndex = findTrackingContextIndex(
    contexts,
    'signup_core_services'
  );

  // Should never happen
  if (coreServiceContextIndex === -1) {
    return contexts;
  }

  const coreServiceContext = contexts[coreServiceContextIndex]
    .data as SignupCoreServicesEntity;
  const {
    selectQuote: {
      monthlyCost: { value: totalPrice },
      quotes,
    },
  } = selectResult;

  if (!coreServiceContext.broadband) {
    contexts[coreServiceContextIndex].data.broadband = true;
    contexts[coreServiceContextIndex].data.number_of_core_services++;
  }

  const updatedBroadbandPrice = getBroadbandMonthlyCost(quotes as Quote[]);

  if (updatedBroadbandPrice) {
    contexts[coreServiceContextIndex].data.broadband_price =
      parseValueToPennies(updatedBroadbandPrice);
  }

  contexts[coreServiceContextIndex].data.total_price =
    parseValueToPennies(totalPrice);
  contexts[
    coreServiceContextIndex
  ].data.core_service_group_quantity_combinations =
    getGroupCombinations(coreServiceContext);

  return contexts;
};

const useBroadbandSelectTracking = (basket: Nullable<BundleBasketData>) => {
  const { trackingContexts } = useTrackingContext();
  const trackSignupBroadbandPageSubmittedOnce =
    useTrackSignupBroadbandAddedToBasketOnce();

  const getSelectedQuote = useCallback(
    (tariffId: string) => getSelectedQuoteUnwrapped(basket)(tariffId),
    [basket]
  );

  const handleSelectedBroadbandItemsForEventTracking = (
    values: BroadbandFormValues,
    data: SelectBroadbandQuoteMutation
  ) => {
    const selectedBroadbandItems = {
      tariff: getSelectedQuote(values.selectedTariff)?.product.name || '',
      whole_home_wifi: values.wifiUpgrade,
      landline_provided: !!values.landline,
      call_package:
        values.callPackage === BooleanSelection.Yes && values.callPackageId
          ? values.callPackageId
          : 'No Call Package',
    };

    const updatedTrackingContexts = updateTrackingContext(
      trackingContexts,
      data
    );

    trackSignupBroadbandPageSubmittedOnce(selectedBroadbandItems, {
      contexts: [...updatedTrackingContexts, ...buildOptimizelyEntity()],
    });
  };

  return { handleSelectedBroadbandItemsForEventTracking };
};

export { useBroadbandSelectTracking };
